/**
 * A money or percent figure that comes from a RESULTS TILE on the brand's own page
 * ("Data Centers. $5M. Tax Savings. Texas.") is one client's reported result. In
 * paid copy it must be attributed to the brand's report of that client, never
 * stated as a general fact ("save $5M") and never promised to the reader
 * ("you'll save $5M"). `findSelfReportedStatisticMisuse` only fires on wording
 * such as "average", so a bare "$5M in tax savings" passed as if it were true of
 * everyone.
 *
 * Pure. The caller decides what a finding does (the Concept variants gate blocks).
 */

import type { AdCopyClaimsField, AdCopyClaimsFinding } from './ad-copy-claims';

const FIGURE = /[$£€]\s?\d[\d,]*(?:\.\d+)?\s?(?:million|billion|thousand|[kmb])?\b|\d[\d,.]*\s?%/gi;

const WORD_SCALE: Record<string, string> = { million: 'm', billion: 'b', thousand: 'k' };

function figureKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/(million|billion|thousand)$/, (word) => WORD_SCALE[word] ?? word);
}

function figuresIn(text: string): string[] {
  return [...text.matchAll(FIGURE)].map((match) => figureKey(match[0]));
}

/**
 * The results tiles that ARE the Concept's own result: a tile that states a figure the Concept's
 * hook states. Every other figure on that tile belongs to the same result ("International Data
 * Center Developer. Multi-State Portfolio 40% reduction. $6M annually ..." under a hook that
 * states only the $6M), so the 40% is the Concept's figure and not a borrowed one.
 */
export function conceptOwnResultTiles(tiles: readonly string[], hook: string): string[] {
  const hookFigures = new Set(figuresIn(hook));
  if (hookFigures.size === 0) return [];
  return tiles.filter((tile) => figuresIn(tile).some((figure) => hookFigures.has(figure)));
}

/** Short, dotted fragments that carry a money/percent figure: a tile, not a sentence of prose. */
export function isResultTile(source: string): boolean {
  const text = source.trim();
  if (text.length === 0 || text.length > 140) return false;
  const parts = text.split(/\.\s+/).filter(Boolean);
  if (parts.length < 3) return false;
  if (!parts.every((part) => part.split(/\s+/).length <= 8)) return false;
  return figuresIn(text).length > 0;
}

const RESULTS_DISCLAIMER = /^results?\s+(?:may\s+|will\s+)?vary\b/i;
const RESULTS_VARY_ANYWHERE = /\bresults?\s+(?:may\s+|will\s+)?vary\b/i;

/**
 * The source's own "Results vary by jurisdiction, property type, and documentation quality."
 * sentence, verbatim, from whichever evidence line carries it. The page prints it beside the
 * client results; null when no source states one, so nothing is ever written for the brand.
 */
export function resultsDisclaimerOf(sources: readonly string[]): string | null {
  for (const source of sources) {
    for (const sentence of source.split(/(?<=[.!?])\s+/)) {
      const text = sentence.trim();
      if (RESULTS_DISCLAIMER.test(text) && text.length <= 200) return text;
    }
  }
  return null;
}

/** Does the text already carry a "results vary" qualifier of its own? */
export function statesResultsVary(text: string): boolean {
  return RESULTS_VARY_ANYWHERE.test(text);
}

/** Does the caption state a figure that a results tile among the sources reports? */
export function statesTileResult(caption: string, sources: readonly string[]): boolean {
  const captionFigures = new Set(figuresIn(caption));
  if (captionFigures.size === 0) return false;
  return sources.some(
    (source) => isResultTile(source) && figuresIn(source).some((f) => captionFigures.has(f)),
  );
}

const FUNCTION_WORDS = new Set(
  'a an the of and or to in on for with by at from that this these those is are was were be as its their your our'.split(
    ' ',
  ),
);

function singularWord(word: string): string {
  return word.length > 3 && /[^s]s$/i.test(word) ? word.slice(0, -1) : word;
}

function contentWords(text: string): string[] {
  const words: string[] = text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  return words.filter((word) => word.length >= 3 && !FUNCTION_WORDS.has(word)).map(singularWord);
}

function tileParts(tile: string): string[] {
  return tile
    .trim()
    .split(/\.\s+/)
    .map((part) => part.replace(/\.$/, '').trim())
    .filter(Boolean);
}

/**
 * The asset, industry or service a results tile is about: its first fragment
 * ("Data Centers", "Cement", "Energy / OFS"). The outcome label and the place
 * are not the subject, so "Valuation Reduction" never makes a tile relevant to
 * a Concept that merely mentions valuations.
 */
export function tileSubject(tile: string): string {
  return tileParts(tile)[0] ?? '';
}

/**
 * Is this results tile about what the Concept is about? True when the tile's
 * subject shares a content word with the Concept text, or the Concept already
 * states the tile's own figure. With no Concept text there is nothing to
 * compare against, and the answer is true: unknown is never a failure.
 */
export function isTileRelevantTo(tile: string, subjectText: string | null | undefined): boolean {
  const subject = subjectText?.trim();
  if (!subject) return true;
  const figures = new Set(figuresIn(subject));
  if (figuresIn(tile).some((figure) => figures.has(figure))) return true;
  const concept = new Set(contentWords(subject));
  return contentWords(tileSubject(tile)).some((word) => concept.has(word));
}

function keepAcronyms(phrase: string): string {
  return phrase
    .split(/\s+/)
    .map((word) =>
      /^[A-Z0-9/&]{2,}$/.test(word) || /[$£€\d]/.test(word) ? word : word.toLowerCase(),
    )
    .join(' ');
}

function singularPhrase(phrase: string): string {
  return phrase
    .split(/\s+/)
    .map((word, index, all) => (index === all.length - 1 ? singularWord(word) : word))
    .join(' ');
}

const SAVINGS_LABEL = /\b(?:savings?|saving)\s*$/i;
const OUTCOME_LABEL = /\b(?:savings?|saved|saving|reduction|reduced|recover\w*|refund\w*)\b/i;
const PERIOD_PREFIX = /^per\s+(year|month|quarter|annum)\b\s*/i;
const MULTIPLIER = /\s*\(\s*[x×]\s*\d+\s*\)\s*/gi;

/**
 * A results tile's "(x3)" multiplier removed from any text it reached. The page does not say
 * whether it means three years or three projects, so no copy, hook or evidence line may print it.
 */
export function stripTileMultiplier(text: string): string {
  return text
    .replace(MULTIPLIER, ' ')
    .replace(/ {2,}/g, ' ')
    .replace(/ ([.,;:!?])/g, '$1')
    .trim();
}

/**
 * The outcome clause of a tile: "$5M" + "Tax Savings" is "saved $5M in tax"
 * (never "in tax savings", which says savings twice); "$2M" + "Per Year (x3)
 * Property Tax Savings" is "saved $2M a year in property tax". The "(x3)" is
 * dropped: the page does not say whether it means three years or three
 * projects, so no recurrence is stated. Null when the label is not an outcome.
 */
function outcomeClause(figure: string, rawLabel: string): string | null {
  if (!OUTCOME_LABEL.test(rawLabel)) return null;
  let label = rawLabel.replace(MULTIPLIER, ' ').replace(/\s+/g, ' ').trim();
  let period = '';
  const periodMatch = PERIOD_PREFIX.exec(label);
  if (periodMatch) {
    period =
      periodMatch[1]!.toLowerCase() === 'annum' ? ' a year' : ` a ${periodMatch[1]!.toLowerCase()}`;
    label = label.slice(periodMatch[0].length).trim();
  }
  if (SAVINGS_LABEL.test(label)) {
    const noun = keepAcronyms(label.replace(SAVINGS_LABEL, '').trim());
    return noun ? `saved ${figure}${period} in ${noun}` : `saved ${figure}${period}`;
  }
  return `achieved a ${figure}${period} ${keepAcronyms(label)}`;
}

function withArticle(phrase: string): string {
  return `${/^(?:[aeiou]|8\d*%|11\D|18\D)/i.test(phrase) ? 'an' : 'a'} ${phrase}`;
}

/**
 * The attributed form of one results tile, composed only from the tile's own
 * words: "Data Centers. $5M. Tax Savings. Texas." becomes "ITC Tax reports a
 * Texas data center client saved $5M in tax." Null when the tile has no
 * outcome label to say what was saved, so nothing is ever guessed.
 */
const PERIOD_WORD = /^(?:annually|per\s+year|a\s+year|yearly|each\s+year)\b\s*/i;
const SCOPE_NOUN = /\s+(?:portfolio|portfolios|program|programs)\s*$/i;

interface FigureFragment {
  readonly scope: string;
  readonly figure: string;
  readonly rest: string;
}

function splitAtFigure(part: string): FigureFragment | null {
  const match = FIGURE_ONCE.exec(part);
  if (!match) return null;
  return {
    scope: part.slice(0, match.index).trim(),
    figure: match[0].trim(),
    rest: part.slice(match.index + match[0].length).trim(),
  };
}

const FIGURE_ONCE = new RegExp(FIGURE.source, 'i');

/**
 * A tile that carries BOTH a money figure and a percent figure, in either
 * order and with a scope fragment before the percent: "International Data
 * Center Developer. Multi-State Portfolio 40% reduction. $6M annually in
 * property tax savings." becomes "ITC Tax reports a multi-state data center
 * developer saved $6M a year in property tax." Null when
 * the tile is not of that shape, so the single-figure path handles it.
 */
function mixedFigureSentence(parts: readonly string[], who: string): string | null {
  const fragments = parts.map((part) => ({ part, split: splitAtFigure(part) }));
  const money = fragments.find((f) => f.split && /^[$£€]/.test(f.split.figure));
  const percent = fragments.find((f) => f.split && /%$/.test(f.split.figure));
  if (!money?.split || !percent?.split) return null;
  if (!OUTCOME_LABEL.test(percent.split.rest) || !OUTCOME_LABEL.test(money.split.rest)) return null;
  const subject = parts[0]!;
  if (subject === money.part || subject === percent.part) return null;

  const periodMatch = PERIOD_WORD.exec(money.split.rest);
  const period = periodMatch ? ' a year' : '';
  const afterPeriod = money.split.rest.slice(periodMatch?.[0].length ?? 0).replace(/^in\s+/i, '');
  const noun = keepAcronyms(afterPeriod.replace(SAVINGS_LABEL, '').trim());
  if (!noun) return null;

  const qualifier = percent.split.scope.replace(SCOPE_NOUN, '').trim();
  const kindBase = qualifier ? subject.replace(/^international\s+/i, '') : subject;
  const kind = keepAcronyms(singularPhrase(kindBase));
  const kindWithScope = qualifier ? `${keepAcronyms(qualifier)} ${kind}` : kind;
  // The percent stays a separate clause, never "cut its property tax by ..., a 40% reduction":
  // the tile does not say what the 40% measures (on ITC's page, assessed value), so joining it to
  // the tax figure would state more than the source does (live check r16).
  // The percent is left out: the tile does not say what it measures (on ITC's page it is assessed
  // value), and any phrasing beside the tax figure reads as a cut in tax (live checks r16, r17).
  return `${who} reports ${withArticle(kindWithScope)} saved ${money.split.figure}${period} in ${noun}.`;
}

export function attributedTileSentence(tile: string, brandName: string | null): string | null {
  const parts = tileParts(tile);
  if (parts.length < 3) return null;
  const mixed = mixedFigureSentence(parts, brandName?.trim() || 'The brand');
  if (mixed) return mixed;
  const figureIndex = parts.findIndex((part) => figuresIn(part).length > 0);
  if (figureIndex < 1) return null;
  const subject = parts[0]!;
  const label = parts[figureIndex + 1];
  if (!label) return null;
  const figure = parts[figureIndex]!;
  const outcome = outcomeClause(figure, label);
  if (!outcome) return null;
  const place = parts[figureIndex + 2];
  const who = brandName?.trim() || 'The brand';
  const placeWord = place && place.split(/\s+/).length <= 3 ? `${place} ` : '';
  const kind = keepAcronyms(singularPhrase(subject));
  return `${who} reports ${withArticle(`${placeWord}${kind}`)} client ${outcome}.`;
}

/** Words that already name a kind of client, so "client" is not added after them. */
const ROLE_NOUN =
  /(?:developer|owner|operator|company|firm|manufacturer|provider|client|business)$/i;

/**
 * A slash-jargon industry label read as English: "Energy / OFS" becomes "Energy". The first
 * fragment that is not a bare abbreviation wins; no abbreviation is ever expanded, because the
 * source does not expand it.
 */
function plainIndustry(subject: string): string {
  const fragments = subject
    .split('/')
    .map((fragment) => fragment.trim())
    .filter(Boolean);
  if (fragments.length === 0) return subject.trim();
  return fragments.find((fragment) => !/^[A-Z0-9&]{2,}$/.test(fragment)) ?? fragments[0]!;
}

/** The place a tile names, narrowed to its last comma fragment: "Harris County, Texas" is "Texas". */
function plainPlace(place: string | undefined): string {
  if (!place || figuresIn(place).length > 0) return '';
  const last = place.split(',').pop()?.trim() ?? '';
  return last && last.split(/\s+/).length <= 2 ? last : '';
}

interface TileClientKind {
  readonly qualifier: string;
  readonly kind: string;
}

/**
 * The client kind a tile names and the scope it carries ("International Data Center Developer.
 * Multi-State Portfolio 40% reduction." is a "data center developer" with the scope "multi-state").
 * The scope is empty when the tile names none.
 */
function tileClientKind(parts: readonly string[]): TileClientKind | null {
  const subject = parts[0];
  if (!subject || figuresIn(subject).length > 0) return null;
  const scoped = parts
    .slice(1)
    .map(splitAtFigure)
    .find((split) => split !== null && split.scope !== '' && /%$/.test(split.figure));
  const qualifier = scoped ? scoped.scope.replace(SCOPE_NOUN, '').trim() : '';
  const base = qualifier ? subject.replace(/^international\s+/i, '') : plainIndustry(subject);
  const kind = keepAcronyms(singularPhrase(base));
  return kind ? { qualifier: qualifier ? keepAcronyms(qualifier) : '', kind } : null;
}

/**
 * The client kind with its scope as a headline may state it ("multi-state data center
 * developer"), lower-cased, for each results tile among the sources that names a scope. A
 * headline that carries one of these is specific to this brand: a competitor has no such client.
 */
export function scopedClientKinds(sources: readonly string[]): string[] {
  const out: string[] = [];
  for (const source of sources) {
    if (!isResultTile(source)) continue;
    const found = tileClientKind(tileParts(source));
    if (found?.qualifier) out.push(`${found.qualifier} ${found.kind}`.toLowerCase());
  }
  return out;
}

const COMPOSED_HEADLINE_MAX = 70;

/**
 * A plain headline composed only from a results tile's own words, leading with its money figure
 * and naming the client it belongs to: "Energy / OFS. $10M. Property Tax Savings. Harris County,
 * Texas." becomes "$10M property tax saved for a Texas energy client", and "International Data
 * Center Developer. Multi-State Portfolio 40% reduction. $6M annually in property tax savings."
 * becomes "$6M a year in property tax saved for a multi-state data center developer". The headline
 * states one client's result as that client's, never as a promise; a second figure (the percent)
 * is left for the intro text. Null when the tile has no money figure or no savings noun, so
 * nothing is ever guessed.
 */
export function composeTileHeadline(tile: string): string | null {
  const parts = tileParts(tile);
  if (parts.length < 3) return null;
  const moneyIndex = parts.findIndex((part) => {
    const split = splitAtFigure(part);
    return split !== null && /^[$£€]/.test(split.figure);
  });
  if (moneyIndex < 1) return null;
  const money = splitAtFigure(parts[moneyIndex]!)!;
  if (money.scope !== '') return null;

  const rawLabel = money.rest || parts[moneyIndex + 1] || '';
  const label = rawLabel.replace(MULTIPLIER, ' ').replace(/\s+/g, ' ').trim();
  if (!label || !OUTCOME_LABEL.test(label) || !SAVINGS_LABEL.test(label)) return null;
  let period = '';
  let noun = label;
  const prefix = PERIOD_PREFIX.exec(noun);
  const word = PERIOD_WORD.exec(noun);
  if (prefix) {
    period = ` a ${prefix[1]!.toLowerCase() === 'annum' ? 'year' : prefix[1]!.toLowerCase()}`;
    noun = noun.slice(prefix[0].length);
  } else if (word) {
    period = ' a year';
    noun = noun.slice(word[0].length);
  }
  noun = keepAcronyms(
    noun
      .replace(/^in\s+/i, '')
      .replace(SAVINGS_LABEL, '')
      .trim(),
  );
  if (!noun) return null;

  const found = tileClientKind(parts);
  if (!found) return null;
  const place = plainPlace(money.rest ? undefined : parts[moneyIndex + 2]);
  const build = (qualifier: string, withPeriod: boolean): string => {
    const scope = qualifier || place;
    const kind = ROLE_NOUN.test(found.kind) ? found.kind : `${found.kind} client`;
    const phrase = withArticle(`${scope ? `${scope} ` : ''}${kind}`);
    return `${money.figure}${withPeriod ? period : ''} ${withPeriod && period ? 'in ' : ''}${noun} saved for ${phrase}`;
  };
  for (const [qualifier, withPeriod] of [
    [found.qualifier, true],
    ['', true],
    ['', false],
  ] as const) {
    const headline = build(qualifier, withPeriod);
    if (headline.length <= COMPOSED_HEADLINE_MAX) return headline;
  }
  return null;
}

function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/);
}

const PROMISE =
  /\b(?:you(?:['’]ll|\s+will|\s+can|\s+could|\s+would|\s+get|\s+save)|your\s+(?:\w+\s+){0,2}(?:will|can|could)|(?:save|saves|get|gets|cut|cuts|recover|recovers|secure|secures|unlock|unlocks|win|wins))\b/i;
const REPORTS = /\b(?:reports?|reported|according\s+to)\b/i;
const CLIENT = /\b(?:clients?|customers?|engagements?)\b/i;

/**
 * A headline may state a results tile's figure when it states it as ONE CLIENT'S result ("$10M
 * property tax saved for a Texas energy client"): it names the client the tile names, and it
 * never promises the figure to the reader. The brand's report is the ad itself, so the headline
 * does not repeat "ITC Tax reports".
 */
const PAST_RESULT = /\b(?:saved|cut|reduced|recovered|refunded)\b/i;

function headlineFramesAClientResult(sentence: string, tile: string): boolean {
  if (PROMISE.test(sentence) || !PAST_RESULT.test(sentence)) return false;
  if (CLIENT.test(sentence)) return true;
  const found = tileClientKind(tileParts(tile));
  return found !== null && sentence.toLowerCase().includes(found.kind.toLowerCase());
}

export function findUnattributedResultFigures(
  fields: readonly AdCopyClaimsField[],
  sources: readonly string[],
  brandName?: string | null,
  conceptSubject?: string | null,
): AdCopyClaimsFinding[] {
  const tiles = sources.filter(isResultTile);
  const tileFigures = new Set(tiles.flatMap(figuresIn));
  if (tileFigures.size === 0) return [];
  const brand = brandName?.trim() ?? '';
  const findings: AdCopyClaimsFinding[] = [];

  for (const { field, text } of fields) {
    if (!text) continue;
    for (const sentence of splitSentences(text)) {
      const used = figuresIn(sentence).filter((figure) => tileFigures.has(figure));
      if (used.length === 0) continue;
      const shown = [...sentence.matchAll(FIGURE)].find((m) => tileFigures.has(figureKey(m[0])));
      const label = shown ? shown[0].trim() : used[0]!;
      const tile = tiles.find((candidate) => figuresIn(candidate).includes(figureKey(label)));
      if (tile && !isTileRelevantTo(tile, conceptSubject)) {
        findings.push({
          field,
          detail: `${field} states ${label}, a result for ${tileSubject(tile)}, which is not what this Concept is about ("${sentence.trim()}"). A result from another industry does not make this ad's case.`,
          fix: `Remove the sentence that states ${label}. Use a result about the Concept's own subject if one is on file; otherwise state no result figure and keep to the Concept's own evidence.`,
          sentence: sentence.trim(),
        });
        continue;
      }
      const rewrite = tile ? attributedTileSentence(tile, brand || null) : null;
      const pasted =
        tile !== undefined &&
        tileParts(tile).some((part) => {
          const split = splitAtFigure(part);
          return (
            split !== null &&
            split.scope !== '' &&
            sentence.toLowerCase().includes(`${split.scope} ${split.figure}`.toLowerCase())
          );
        });
      if (pasted && rewrite) {
        findings.push({
          field,
          detail: `${field} pastes the results tile's own fragments into a sentence instead of composing one ("${sentence.trim()}").`,
          fix: `In ${field}, replace the sentence with exactly: "${rewrite}"`,
          sentence: sentence.trim(),
          rewrite,
        });
        continue;
      }
      const inHeadline = /^headline/i.test(field);
      if (inHeadline && tile && headlineFramesAClientResult(sentence, tile)) continue;
      const fix = inHeadline
        ? `Either frame ${label} in the ${field} as one client's result naming the client ("${label} saved for a [place] [client kind] client"), never as a promise to the reader, or take it out and state it once in the main text as exactly: "${rewrite ?? `${brand || 'The brand'} reports a client saved ${label}`}" and let the headline name the asset, place or service the evidence states.`
        : rewrite
          ? `In ${field}, keep the figure and attribute it. Replace the sentence with exactly: "${rewrite}" Never "save ${label}" or "you'll save ${label}".`
          : `In ${field}, attribute ${label} to ${brand || 'the brand'}'s own report of one client ("${brand || 'The brand'} reports a client saved ${label}"), keeping the figure and using only the words of the source line. Never "save ${label}" or "you'll save ${label}".`;
      const rewriteField = rewrite ? { rewrite } : {};
      const promised = PROMISE.test(sentence.replace(new RegExp(`\\b${REPORTS.source}`, 'i'), ''));
      const clientNamed = CLIENT.test(sentence) || (rewrite !== null && sentence.includes(rewrite));
      const attributed =
        REPORTS.test(sentence) &&
        clientNamed &&
        (brand === '' || sentence.toLowerCase().includes(brand.toLowerCase()));
      if (promised && !(REPORTS.test(sentence) && clientNamed)) {
        findings.push({
          field,
          detail: `${field} states ${label}, one client's reported result, as something the reader will get ("${sentence.trim()}").`,
          fix,
          sentence: sentence.trim(),
          ...rewriteField,
        });
      } else if (!attributed) {
        findings.push({
          field,
          detail: `${field} states ${label}, one client's reported result, as a general fact without saying ${brand || 'the brand'} reports it for a client ("${sentence.trim()}").`,
          fix,
          sentence: sentence.trim(),
          ...rewriteField,
        });
      }
    }
  }
  return findings;
}
